import { memo, useCallback, type MouseEvent as ReactMouseEvent } from "react";
import type { BubbleConfig, BubbleLayout } from "./types";
import { DEFAULT_BUBBLE_LAYOUT } from "./types";
import { useBubbleTooltip } from "./useBubbleTooltip";

/**
 * One bubble inside a diagram SVG. Ported from `_legacy/xaes-fx.js`
 * `_renderBubble`. Renders:
 *
 *   1. Outer filled circle (gradient via `fill`, stroke colour)
 *   2. Inner dashed ring with `.bubble-dash-ring` (CSS-animated spin)
 *   3. Icon glyph (lucide-react inside a `<foreignObject>` — see
 *      `BubbleConfig.icon` for why we don't use Material Symbols)
 *   4. Title lines, divider, body lines
 *   5. Optional sub-label (WS8 phase bubbles)
 *
 * `React.memo` keeps this stable across parent re-renders so the
 * dashed-ring CSS spin and any sibling `<animateMotion>` elements
 * don't restart when ancestor state changes.
 */
export const Bubble = memo(function Bubble({
  config,
  layout = DEFAULT_BUBBLE_LAYOUT,
  highlighted = false,
  onHoverChange,
}: {
  config: BubbleConfig;
  layout?: BubbleLayout;
  /** Cross-highlight from a linked workshop being hovered elsewhere
   *  (e.g. a Program Rollup row) — boosts stroke/glow, no layout shift. */
  highlighted?: boolean;
  /** Fired on hover enter/leave so an ancestor can drive a linked
   *  surface's highlight (e.g. Program Rollup workshop rows). */
  onHoverChange?: (hovering: boolean) => void;
}) {
  const d = config;
  const { spacing: g, titleLineH: tLH, bodyLineH: bLH, labelFontSize = 9 } =
    layout;

  const tf = d.titleFontSize;
  const bf = d.bodyFontSize;
  const iconH = 24 * d.iconScale;
  const hasBody = d.bodyLines.length > 0;

  // Total content height: icon → title [→ divider → body]. Skip the
  // body band when bodyLines is empty (dashboard theme diagrams are
  // title-only so labels stay inside the ring).
  const titleH = tf + (d.titleLines.length - 1) * tLH;
  const bodyH = hasBody
    ? g + g + bf + (d.bodyLines.length - 1) * bLH
    : 0;
  const contentH = iconH + g + titleH + bodyH;

  const iconTy = d.cy - contentH / 2;
  const iBot = iconTy + iconH;
  const t1Y = iBot + g + tf;
  const tLastY = t1Y + (d.titleLines.length - 1) * tLH;
  const divY = tLastY + g;
  const b1Y = divY + g + bf;

  const iconSize = Math.round(24 * d.iconScale);
  const iconX = d.iconTx !== undefined ? d.iconTx : d.cx - iconSize / 2;

  const Icon = d.icon;
  const labelY = d.label ? d.cy + d.outerR + 13 : 0;

  // Tooltip content: explicit tipTitle / tipBody preferred; otherwise
  // fall back to the bubble's own titleLines + bodyLines so any bubble
  // without curated copy still gets a hover affordance. The accent is
  // the bubble's stroke colour — already a theme-aware CSS variable
  // like `var(--accent-sky)`.
  const tipTitle = d.tipTitle ?? d.titleLines.join(" ");
  const tipBody = d.tipBody ?? d.bodyLines.join(" ");
  const hasTip = tipTitle.length > 0;
  const showTip = useBubbleTooltip((s) => s.show);
  const hideTip = useBubbleTooltip((s) => s.hide);

  const onEnter = useCallback(
    (e: ReactMouseEvent<SVGGElement>) => {
      onHoverChange?.(true);
      if (!hasTip) return;
      showTip({
        title: tipTitle,
        body: tipBody,
        x: e.clientX,
        y: e.clientY,
        accent: d.stroke,
      });
    },
    [hasTip, showTip, tipTitle, tipBody, d.stroke, onHoverChange],
  );

  const onMove = useCallback(
    (e: ReactMouseEvent<SVGGElement>) => {
      if (!hasTip) return;
      showTip({
        title: tipTitle,
        body: tipBody,
        x: e.clientX,
        y: e.clientY,
        accent: d.stroke,
      });
    },
    [hasTip, showTip, tipTitle, tipBody, d.stroke],
  );

  const onLeave = useCallback(() => {
    onHoverChange?.(false);
    if (!hasTip) return;
    hideTip();
  }, [hasTip, hideTip, onHoverChange]);

  const hasHover = hasTip || onHoverChange !== undefined;

  return (
    <g
      id={d.id}
      data-tip-title={d.tipTitle}
      data-tip-body={Array.isArray(d.tipBody) ? d.tipBody.join(" | ") : d.tipBody}
      onMouseEnter={hasHover ? onEnter : undefined}
      onMouseMove={hasTip ? onMove : undefined}
      onMouseLeave={hasHover ? onLeave : undefined}
      style={{
        filter: highlighted ? `drop-shadow(0 0 10px ${d.stroke})` : undefined,
        transition: "filter 0.15s",
      }}
    >
      {/* Outer filled circle */}
      <circle
        cx={d.cx}
        cy={d.cy}
        r={d.outerR}
        fill={d.fill}
        stroke={d.stroke}
        strokeWidth={highlighted ? d.outerStrokeW * 2 : d.outerStrokeW}
        opacity={d.outerOp}
        filter={d.filter}
        style={{ transition: "stroke-width 0.15s" }}
      />

      {/* Completion fill — liquid-style rise from the bottom of the
          outer circle, clipped to the circle's own shape so it never
          spills past the ring regardless of percentage. */}
      {d.fillPercent !== undefined && (
        <>
          <clipPath id={`${d.id}-fill-clip`}>
            <circle cx={d.cx} cy={d.cy} r={d.outerR} />
          </clipPath>
          <rect
            x={d.cx - d.outerR}
            y={d.cy + d.outerR - 2 * d.outerR * Math.max(0, Math.min(1, d.fillPercent))}
            width={d.outerR * 2}
            height={2 * d.outerR * Math.max(0, Math.min(1, d.fillPercent))}
            fill="var(--accent-true-blue)"
            opacity={0.38}
            clipPath={`url(#${d.id}-fill-clip)`}
            style={{ transition: "y 0.4s ease, height 0.4s ease" }}
            pointerEvents="none"
          />
        </>
      )}

      {/* Spinning dashed ring — rotates around its own bounding-box
          centre via `transform-box: fill-box` in index.css.
          No inline transform-origin: that would conflict with fill-box
          and push the pivot off-screen. */}
      <circle
        cx={d.cx}
        cy={d.cy}
        r={d.innerR}
        fill="none"
        stroke={d.stroke}
        strokeWidth={d.ringStrokeW}
        strokeDasharray="3,4"
        opacity={d.ringOp}
        className="bubble-dash-ring"
      />

      {/* Icon */}
      {Icon && (
        <foreignObject x={iconX} y={iconTy} width={iconSize} height={iconSize}>
          <div
            style={{
              width: "100%",
              height: "100%",
              color: d.stroke,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Icon style={{ width: "100%", height: "100%" }} strokeWidth={1.5} aria-hidden />
          </div>
        </foreignObject>
      )}

      {/* Title lines */}
      {d.titleLines.map((line, i) => (
        <text
          key={`t-${i}`}
          x={d.cx}
          y={(t1Y + i * tLH).toFixed(1)}
          textAnchor="middle"
          fill={d.stroke}
          fontSize={d.titleFontSize}
          fontFamily="Inter, sans-serif"
          fontWeight={700}
          letterSpacing={d.letterSpacing}
        >
          {line}
        </text>
      ))}

      {/* Divider + body — omitted when bodyLines is empty. */}
      {hasBody && (
        <>
          <line
            x1={d.cx - d.dividerHW}
            y1={divY.toFixed(1)}
            x2={d.cx + d.dividerHW}
            y2={divY.toFixed(1)}
            stroke={d.stroke}
            strokeWidth={0.5}
            opacity={0.3}
          />
          {d.bodyLines.map((line, i) => (
            <text
              key={`b-${i}`}
              x={d.cx}
              y={(b1Y + i * bLH).toFixed(1)}
              textAnchor="middle"
              fill="#FFFFFF"
              fontSize={d.bodyFontSize}
              fontFamily="Inter, sans-serif"
              fontWeight={400}
              opacity={1}
            >
              {line}
            </text>
          ))}
        </>
      )}

      {/* Optional WS8-style sub-label */}
      {d.label && (
        <g className="bubble-child-label" pointerEvents="none">
          <line
            x1={d.cx - 31}
            y1={labelY}
            x2={d.cx + 31}
            y2={labelY}
            stroke={d.label.color ?? "var(--accent-melon)"}
            strokeWidth={1}
            opacity={0.95}
          />
          <text
            x={d.cx}
            y={labelY + 13}
            textAnchor="middle"
            fill={d.label.color ?? "var(--accent-melon)"}
            fontFamily="Inter, sans-serif"
            fontWeight={700}
            letterSpacing={0.5}
            fontSize={labelFontSize}
          >
            {d.label.title}
          </text>
          {d.label.bodyLines.map((line, i) => (
            <text
              key={`lbl-${i}`}
              x={d.cx}
              y={labelY + 24 + i * (labelFontSize + 2)}
              textAnchor="middle"
              fill="var(--text-primary)"
              fontFamily="Inter, sans-serif"
              fontWeight={500}
              fontSize={labelFontSize}
            >
              {line}
            </text>
          ))}
        </g>
      )}
    </g>
  );
});
